// The keep-alive of a visible page (R1706, ui-map-interface.md §16.12).
//
// A browser keeps one HTTP/3 connection per origin, and HTTP/3 rides UDP.
// A home router drops the UDP mapping of a flow after about 30 s of
// silence. A page that talks only on its WebSocket, or only to another
// host, leaves the connection to its own origin silent, and the next
// request on it waits with no answer. So while the page is visible it
// reads one small same-origin document every `KEEPALIVE_MS`, and the flow
// is never silent long enough.
//
// The document is `/maintenance.json`, because every host already answers
// it from nginx with no work: 404 while the gate is down, 200 while it
// stands. The keep-alive IGNORES the answer. The gate has its own reader
// (`net/maintenance.ts`) and its own cover, and this module must never
// decide that the game is closed. `cache: 'no-store'`, so the request
// reaches the network and not the browser cache. The read keeps the
// default credentials of a same-origin fetch: a browser may keep the
// requests without credentials on a connection of their own, and then the
// keep-alive would hold the wrong connection open.
//
// A hidden page reads nothing: the timer stops on `visibilitychange`, and
// the first read after the page shows again goes out at once, because a
// hidden page may have left the connection silent for minutes.

import { KEEPALIVE_MS } from '../gen/presentation';

/** What the keep-alive reads outside itself. Each one has a real default. */
export interface KeepAliveDeps {
  doc?: Pick<Document, 'hidden' | 'addEventListener' | 'removeEventListener'>;
  ping?: () => void;
  setInterval?: (fn: () => void, ms: number) => unknown;
  clearInterval?: (id: unknown) => void;
  periodMs?: number;
}

/** The address the keep-alive reads. */
export const KEEPALIVE_URL = '/maintenance.json';

/** One read, with the answer and every failure ignored. */
function ping(): void {
  void fetch(KEEPALIVE_URL, { cache: 'no-store' })
    .then((res) => { void res.body?.cancel(); }, () => { /* a failure says nothing here */ });
}

/**
 * Starts the keep-alive and answers the function that stops it. A page
 * calls it once, at boot.
 */
export function startKeepAlive(deps: KeepAliveDeps = {}): () => void {
  const doc = deps.doc ?? (typeof document === 'undefined' ? null : document);
  if (doc === null) return () => {};
  const send = deps.ping ?? ping;
  const set = deps.setInterval ?? ((fn, ms) => setInterval(fn, ms));
  const clear = deps.clearInterval ?? ((id) => clearInterval(id as ReturnType<typeof setInterval>));
  const period = deps.periodMs ?? KEEPALIVE_MS;
  let timer: unknown = null;
  const stop = (): void => {
    if (timer !== null) clear(timer);
    timer = null;
  };
  const sync = (): void => {
    if (doc.hidden) { stop(); return; }
    if (timer !== null) return;
    send();
    timer = set(send, period);
  };
  doc.addEventListener('visibilitychange', sync);
  // The page has just loaded its own documents, so the first read waits
  // one period.
  if (!doc.hidden) timer = set(send, period);
  return () => {
    stop();
    doc.removeEventListener('visibilitychange', sync);
  };
}
